import React, {
    useState,
    InputHTMLAttributes,
    useEffect,
    TextareaHTMLAttributes,
    useMemo,
    useCallback,
    useRef,
    MutableRefObject,
    useLayoutEffect,
} from 'react';
import { createStyle } from 'utils/createStyle';

interface CustomInputProps extends InputHTMLAttributes<HTMLInputElement> {}
interface CustomTextareaProps
    extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
    className?: string;
    showCounter?: boolean;
    counterLimit?: number;
}

type CustomTextInputProps = CustomInputProps &
    CustomTextareaProps & {
        label: string;
        errorMessage?: string;
        textarea?: boolean;
        onHasValueChange?: (hasValue: boolean) => void;
    };

const inputWrapperStyle = createStyle(`relative`, { width: '380px' });

export const CustomTextInput = ({
    label,
    errorMessage,
    textarea,
    showCounter,
    counterLimit = 150,
    value,
    onHasValueChange,
    ...props
}: CustomTextInputProps) => {
    const [focused, setFocused] = useState(false);
    const [hasValue, setHasValue] = useState(true);
    const [touched, setTouched] = useState(false);
    const inputRef = useRef<any>();

    const hasError = touched && !hasValue && Boolean(errorMessage);

    useEffect(() => {
        if (onHasValueChange) {
            onHasValueChange(hasValue);
        }
    }, [hasValue, onHasValueChange]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.value = value?.toString() || '';
        }
    }, [value]);

    const handleFocus = () => {
        setTouched(true);
        setFocused(true);
    };
    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        props.onBlur?.(e as any);
        setFocused(false);
        setHasValue(!!value && value.toString().length > 0);
        props.onChange?.(e as any);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const target = e.target as HTMLInputElement | HTMLTextAreaElement;
        if (target.value && target.value.toString().length > counterLimit) {
            return;
        }
        setHasValue(target.value.length > 0);
    };

    const getTopValue = useCallback(() => {
        // focused || hasValue ? '0.75rem' : textarea ? '18px' : '50%'
        if (focused || hasValue) {
            return '0.75rem';
        }
        if (hasError) {
            return '35%';
        }
        if (textarea) {
            return '18px';
        }
        return '50%';
    }, [focused, hasValue, hasError, textarea]);

    const labelStyle = useMemo(
        () =>
            createStyle(
                `
          absolute left-4 
          ${focused ? 'text-xs text-midnight-black' : 'text-grey-2'}
          ${focused || hasValue ? 'text-xs' : ''} 
          transition-all duration-200 bg-white w-11/12
        `,
                {
                    top: getTopValue(),
                    transform: focused || hasValue || textarea ? 'none' : 'translateY(-50%)',
                    fontSize: focused || hasValue ? '14px' : '18px',
                    marginBottom: focused || hasValue ? '0.5rem' : '0',
                }
            ),
        [focused, hasValue, textarea, getTopValue]
    );

    const inputStyle = useMemo(
        () =>
            createStyle(
                `
          w-full px-4 py-5 border rounded-lg border-grey-light
          ${props.className}
          focus:outline-none
          text-base text-grey-darkest
          ${hasError && !focused ? 'border-error' : 'focus:border-brand'}
        `,
                {
                    fontSize: '18px',
                    paddingTop: '33px',
                    paddingBottom: '15px',
                    minHeight: textarea ? '144px' : 'auto',
                    height: 'auto',
                    ...(textarea ? { resize: 'none' } : {}),
                }
            ),
        [props.className, hasError, textarea]
    );

    const InputElement = textarea ? 'textarea' : 'input';

    useEffect(() => {
        value !== '' && value !== undefined ? setHasValue(true) : setHasValue(false);
    }, [value]);

    return (
        <div className={inputWrapperStyle}>
            <label htmlFor={props.name} className={labelStyle}>
                {label}
            </label>

            <InputElement
                {...props}
                id={props.name}
                className={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={(e: React.FocusEvent<any>) => handleChange(e)}
                ref={inputRef}
                defaultValue={value?.toString() || ''}
                maxLength={counterLimit}
            />
            {hasError && !focused && (
                <div className="text-error text-sm pt-2 pl-0">{errorMessage}</div>
            )}
            {showCounter ? <Counter counterLimit={counterLimit} inputEl={inputRef} /> : null}
        </div>
    );
};

const Counter = ({
    inputEl,
    counterLimit,
}: {
    inputEl?: MutableRefObject<any>;
    counterLimit: number;
}) => {
    const value = inputEl?.current?.value || '';
    const [, setTriggerRender] = useState<boolean>(false);

    useLayoutEffect(() => {
        const listener = () => {
            setTriggerRender((v) => !v);
        };

        inputEl?.current.addEventListener('input', listener);
        return () => window.removeEventListener('input', listener);
    }, [inputEl?.current]);

    const getCharCount = () => {
        const stringValue = value?.toString();
        return stringValue?.length ? `${stringValue.length}/${counterLimit}` : null;
    };

    return (
        <span className="flex h-5 place-content-end text-sm text-right text-grey-dark ">
            {getCharCount()}
        </span>
    );
};

export default CustomTextInput;
